import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import { ProductCardSelections } from "@insite/content-library/Widgets/ProductList/ProductCardSelections";
import ProductListActions from "@insite/content-library/Widgets/ProductList/ProductListActions";
import ProductListProductImage from "@insite/content-library/Widgets/ProductList/ProductListProductImage";
import ProductListProductInformation from "@insite/content-library/Widgets/ProductList/ProductListProductInformation";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

type Props = ProductCardSelections & HasProduct & ReturnType<typeof mapStateToProps>;

const mapStateToProps = (state: ApplicationState) => ({
    settingsCollection: getSettingsCollection(state),
});

export interface ProductListProductGridCardStyles {
    wrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    addToListWrapper?: InjectableCss;
    addToListLink?: ProductAddToListLinkStyles;
    parentContainer: InjectableCss;
    infoPriceContainer: InjectableCss;
    sponsoredText?: TypographyPresentationProps;
    partNumbersStyles?: ProductPartNumbersStyles;
}

export const productGridCardStyles: ProductListProductGridCardStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            height: 100%;
            overflow: hidden;
            padding-bottom: 10px;
            padding: 10px;
        `,
    },
    imageWrapper: {
        css: css`
            margin: auto;
            padding: 10px 0;
            width: 100%;
        `,
    },
    addToListWrapper: {
        css: css`
            text-align: center;
            padding-top: 10px;
        `,
    },
    addToListLink: {
        link: {
            css: css`
                font-size: 16px;
                font-weight: 600;
            `,
        },
    },
    parentContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            height: 100%;
            overflow: hidden;
        `,
    },
    infoPriceContainer: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    sponsoredText: {
        css: css`
            font-size: 0.9em;
            opacity: 0.5;
            position: absolute;
            top: 12px;
        `,
    },
    partNumbersStyles: {
        container: {
            css: css`
                flex-shrink: 1;
                flex-grow: 0;
            `,
        },
        erpNumberLabelText: {
            weight: "normal",
            css: css`
            margin-right: 7px;`,
        },
        manufacturerItemLabelText: {
            weight: "normal",
            css: css`
            margin-right: 7px;`,
        },
    },
};

const styles = productGridCardStyles;

const ProductListProductGridCard: FC<Props> = ({
    product,
    showImage,
    showCompare,
    showAttributes,
    showAvailability,
    showBrand,
    showPartNumbers,
    showTitle,
    showAddToList,
    showPrice,
    settingsCollection,
    showImageBadges,
    maximumNumberOfImageBadges,
    showTextBadges,
    maximumNumberOfTextBadges,
}) => {
    return (
        <StyledWrapper {...styles.parentContainer}>
            <StyledWrapper {...styles.wrapper} data-test-selector={`productListProductCard${product.id}`}>
                <StyledWrapper {...styles.imageWrapper}>
                    {product.isSponsored && <Typography {...styles.sponsoredText}>{translate("Sponsored")}</Typography>}
                    <ProductListProductImage
                        showImage={showImage}
                        showCompare={false}
                        showImageBadge={showImageBadges}
                        showTextBadge={showTextBadges}
                    />
                </StyledWrapper>
                <ProductListProductInformation
                    showAttributes={showAttributes}
                    showAvailability={false}
                    showBrand={showBrand}
                    showPartNumbers={showPartNumbers}
                    showTitle={showTitle}
                    showImageBadges={showImageBadges}
                    maxImageBadges={maximumNumberOfImageBadges}
                    showTextBadges={showTextBadges}
                    maxTextBadges={maximumNumberOfTextBadges}
                />
                <ProductListProductImage showImage={false} showCompare={showCompare} />
                <StyledWrapper {...styles.infoPriceContainer}>
                    {showAvailability && settingsCollection.productSettings.showInventoryAvailability && (
                        <StyledWrapper>
                            <ProductContextAvailability />
                        </StyledWrapper>
                    )}
                    <ProductListActions showAddToList={false} showPrice={showPrice} />
                </StyledWrapper>
            </StyledWrapper>
            {showAddToList && (
                <StyledWrapper {...styles.addToListWrapper}>
                    <ProductAddToListLink
                        data-test-selector={`actionsAddToList${product.id}`}
                        extendedStyles={styles.addToListLink}
                    />
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(withProduct(ProductListProductGridCard));
